import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Snackbar,
  Alert,
} from '@mui/material';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PresentToAllIcon from '@mui/icons-material/PresentToAll';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useMutation } from '@tanstack/react-query';
import { slidesService } from '../../services/slidesService';

interface SlideExportButtonProps {
  lessonId: string;
  versionNumber: number;
  onHTMLReady?: (html: string, markdown: string) => void;
}

type ExportFormat = 'html' | 'pdf' | 'pptx';

function triggerDownload(url: string, filename: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener noreferrer';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

export const SlideExportButton: React.FC<SlideExportButtonProps> = ({
  lessonId,
  versionNumber,
  onHTMLReady,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [activeFormat, setActiveFormat] = useState<ExportFormat | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const { mutate: runExport, isPending } = useMutation({
    mutationFn: (format: ExportFormat) => {
      setActiveFormat(format);
      return slidesService.export(lessonId, versionNumber, format);
    },
    onSuccess: (data) => {
      if (data.format === 'html') {
        if (onHTMLReady && data.html) {
          onHTMLReady(data.html, '');
        } else if (data.html) {
          const blob = new Blob([data.html], { type: 'text/html' });
          const url = URL.createObjectURL(blob);
          window.open(url, '_blank', 'noopener,noreferrer');
          setTimeout(() => URL.revokeObjectURL(url), 10_000);
        }
      } else {
        triggerDownload(data.download_url, `slides.${data.format}`);
      }
      setActiveFormat(null);
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Failed to generate slides. Please try again.', severity: 'error' });
      setActiveFormat(null);
    },
  });

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleFormat = (format: ExportFormat) => {
    handleMenuClose();
    runExport(format);
  };

  const formatLabel = activeFormat ? `Generating ${activeFormat.toUpperCase()}…` : 'Export Slides';

  return (
    <>
      <Box sx={{ display: 'flex' }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={isPending ? <CircularProgress size={14} /> : <SlideshowIcon />}
          onClick={() => handleFormat('html')}
          disabled={isPending}
          sx={{ borderRadius: '4px 0 0 4px', borderRight: 0 }}
        >
          {formatLabel}
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={handleMenuOpen}
          disabled={isPending}
          aria-label="Export format options"
          sx={{ borderRadius: '0 4px 4px 0', minWidth: 28, px: 0 }}
        >
          <ArrowDropDownIcon fontSize="small" />
        </Button>
      </Box>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={() => handleFormat('html')}>
          <ListItemIcon><SlideshowIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="HTML (preview in browser)" />
        </MenuItem>
        <MenuItem onClick={() => handleFormat('pdf')}>
          <ListItemIcon><PictureAsPdfIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="PDF (print-ready)" />
        </MenuItem>
        <MenuItem onClick={() => handleFormat('pptx')}>
          <ListItemIcon><PresentToAllIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="PowerPoint (PPTX)" />
        </MenuItem>
      </Menu>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};
